import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { EmblaOptionsType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import React, { useCallback, useEffect, useState } from 'react'
import useBreakpoints from 'src/hooks/useBreakpoints'

interface CarouselProps<T> {
  items: T[]
  onItemClick?: (item: T) => void
  children: (props: {
    item: T
    index: number
    onItemClick?: (item: T) => void
    selected?: boolean
  }) => React.ReactElement
  carouselOptions?: EmblaOptionsType
  carouselOptionsDesktop?: EmblaOptionsType
  carouselOptionsMobile?: EmblaOptionsType
  showButtons?: boolean
  showDots?: boolean
  showButtonsMb?: boolean
  showDotsMb?: boolean
  showButtonsMd?: boolean
  showDotsMd?: boolean
  alignDots?: 'justify-start' | 'justify-center' | 'justify-end'
  /** @deprecated Arrows are now overlaid — this prop is ignored */
  marginButtons?: 2 | 4
  fillAvailable?: boolean
  slideClassName?: string
  /** Vertical position of the arrows (default: the carousel's middle). Card
   *  carousels centre them on the PHOTO instead — at the card's middle they
   *  sat on the promo badge row (2026-10-28). */
  arrowTopClassName?: string
}

function Carousel<T>({
  items = [],
  onItemClick,
  children,
  carouselOptions,
  carouselOptionsDesktop,
  carouselOptionsMobile,
  showDots,
  showButtons,
  showDotsMb,
  showButtonsMb,
  showDotsMd,
  alignDots = 'justify-center',
  showButtonsMd,
  fillAvailable,
  slideClassName,
  arrowTopClassName = 'top-1/2'
}: CarouselProps<T>) {
  const { isDownMd, isUpMd } = useBreakpoints()

  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false)
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false)

  const [viewportRef, embla] = useEmblaCarousel({
    ...carouselOptions,
    ...(isDownMd && carouselOptionsMobile),
    ...(isUpMd && carouselOptionsDesktop)
  })

  useEffect(() => {
    if (!embla) return

    const onSelect = () => {
      setSelectedIndex(embla.selectedScrollSnap())
      setPrevBtnEnabled(embla.canScrollPrev())
      setNextBtnEnabled(embla.canScrollNext())
    }
    const onInit = () => setScrollSnaps(embla.scrollSnapList())

    onInit()
    onSelect()
    embla.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect)

    return () => {
      embla
        .off('reInit', onInit)
        .off('reInit', onSelect)
        .off('select', onSelect)
    }
  }, [embla])

  const scrollPrev = useCallback(() => embla?.scrollPrev(), [embla])
  const scrollNext = useCallback(() => embla?.scrollNext(), [embla])
  const scrollTo = useCallback(
    (index: number) => embla?.scrollTo(index),
    [embla]
  )

  const handleItemClick = (item: T) => {
    if (onItemClick) onItemClick(item)
  }

  const buttons =
    showButtons || (showButtonsMb && isDownMd) || (showButtonsMd && isUpMd)
  const dots = showDots || (showDotsMb && isDownMd) || (showDotsMd && isUpMd)

  const arrowClass = `absolute ${arrowTopClassName} z-10 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-white-500/90 shadow-md transition-opacity hover:bg-white-500 md:opacity-0 md:group-hover:opacity-100`

  return (
    <div className={`group relative ${fillAvailable ? 'w-full' : ''}`}>
      <div className="overflow-hidden" ref={viewportRef}>
        <div className="flex py-2 tap-highlight-transparent select-none">
          {items.map((item, index) => (
            <div
              className={`relative min-w-0 shrink-0 px-[10px] ${slideClassName ?? ''}`}
              key={index}
              onClick={() => handleItemClick(item)}
            >
              {children({
                item,
                index,
                onItemClick,
                selected: index === selectedIndex
              })}
            </div>
          ))}
        </div>
      </div>

      {buttons && prevBtnEnabled && (
        <button
          onClick={scrollPrev}
          className={`${arrowClass} left-2`}
          aria-label="Previous"
        >
          <ArrowBackIosNewIcon
            style={{ fontSize: 14 }}
            className="text-black-400"
          />
        </button>
      )}

      {buttons && nextBtnEnabled && (
        <button
          onClick={scrollNext}
          className={`${arrowClass} right-2`}
          aria-label="Next"
        >
          <ArrowForwardIosIcon
            style={{ fontSize: 14 }}
            className="text-black-400"
          />
        </button>
      )}

      {dots && scrollSnaps.length > 1 && (
        <div className={`mt-3 flex ${alignDots}`}>
          {scrollSnaps.map((_, index) => (
            <button
              className={`mx-1 size-1.5 rounded-full transition-colors ${
                index === selectedIndex ? 'bg-blue-500' : 'bg-grey-400'
              }`}
              key={index}
              onClick={() => scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default React.memo(Carousel) as typeof Carousel
